import { useState, useMemo, useCallback } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import googleAdsService, {
  GoogleAdsConnectionStatus,
  GoogleAdsCampaign,
  GoogleAdsOverview,
} from '@/services/googleAds';
import CEOxGoogleAdsComponent, { DateFilter, DatePreset } from './CEOxGoogleAds.component';

// ── MOCK DATA (remove after Google verification) ──────────────────────
const USE_MOCK = true;

const MOCK_CAMPAIGNS: GoogleAdsCampaign[] = [
  {
    campaignId: '1001',
    campaignName: 'Brand Awareness - US',
    status: 'ENABLED',
    campaignType: 'SEARCH',
    impressions: 184200,
    clicks: 7368,
    cost: 12450.60,
    conversions: 312,
    conversionValue: 46800,
    cpc: 1.69,
    cpa: 39.91,
    ctr: 4.0,
    cvr: 4.23,
  },
  {
    campaignId: '1002',
    campaignName: 'Lead Gen - Software Engineers',
    status: 'ENABLED',
    campaignType: 'SEARCH',
    impressions: 92400,
    clicks: 4158,
    cost: 8732.40,
    conversions: 187,
    conversionValue: 37400,
    cpc: 2.10,
    cpa: 46.70,
    ctr: 4.5,
    cvr: 4.50,
  },
  {
    campaignId: '1003',
    campaignName: 'Retargeting - Website Visitors',
    status: 'ENABLED',
    campaignType: 'DISPLAY',
    impressions: 342000,
    clicks: 5130,
    cost: 3591.00,
    conversions: 98,
    conversionValue: 14700,
    cpc: 0.70,
    cpa: 36.64,
    ctr: 1.5,
    cvr: 1.91,
  },
  {
    campaignId: '1004',
    campaignName: 'Competitor Keywords',
    status: 'ENABLED',
    campaignType: 'SEARCH',
    impressions: 67800,
    clicks: 2034,
    cost: 5695.20,
    conversions: 71,
    conversionValue: 10650,
    cpc: 2.80,
    cpa: 80.21,
    ctr: 3.0,
    cvr: 3.49,
  },
  {
    campaignId: '1005',
    campaignName: 'YouTube - Product Demo',
    status: 'ENABLED',
    campaignType: 'VIDEO',
    impressions: 521000,
    clicks: 10420,
    cost: 4168.00,
    conversions: 54,
    conversionValue: 8100,
    cpc: 0.40,
    cpa: 77.19,
    ctr: 2.0,
    cvr: 0.52,
  },
  {
    campaignId: '1006',
    campaignName: 'Performance Max - Q1',
    status: 'ENABLED',
    campaignType: 'PERFORMANCE_MAX',
    impressions: 278000,
    clicks: 6672,
    cost: 9341.00,
    conversions: 234,
    conversionValue: 42120,
    cpc: 1.40,
    cpa: 39.92,
    ctr: 2.4,
    cvr: 3.51,
  },
  {
    campaignId: '1007',
    campaignName: 'Discovery - Tech Decision Makers',
    status: 'PAUSED',
    campaignType: 'DISCOVERY',
    impressions: 145000,
    clicks: 2900,
    cost: 2610.00,
    conversions: 38,
    conversionValue: 5700,
    cpc: 0.90,
    cpa: 68.68,
    ctr: 2.0,
    cvr: 1.31,
  },
];

const MOCK_OVERVIEW: GoogleAdsOverview = {
  totalImpressions: MOCK_CAMPAIGNS.reduce((s, c) => s + c.impressions, 0),
  totalClicks: MOCK_CAMPAIGNS.reduce((s, c) => s + c.clicks, 0),
  totalCost: MOCK_CAMPAIGNS.reduce((s, c) => s + c.cost, 0),
  totalConversions: MOCK_CAMPAIGNS.reduce((s, c) => s + c.conversions, 0),
  totalConversionValue: MOCK_CAMPAIGNS.reduce((s, c) => s + c.conversionValue, 0),
  avgCpc: MOCK_CAMPAIGNS.reduce((s, c) => s + c.cost, 0) / MOCK_CAMPAIGNS.reduce((s, c) => s + c.clicks, 0),
  avgCpa: MOCK_CAMPAIGNS.reduce((s, c) => s + c.cost, 0) / MOCK_CAMPAIGNS.reduce((s, c) => s + c.conversions, 0),
  avgCtr: (MOCK_CAMPAIGNS.reduce((s, c) => s + c.clicks, 0) / MOCK_CAMPAIGNS.reduce((s, c) => s + c.impressions, 0)) * 100,
  avgCvr: (MOCK_CAMPAIGNS.reduce((s, c) => s + c.conversions, 0) / MOCK_CAMPAIGNS.reduce((s, c) => s + c.clicks, 0)) * 100,
  campaigns: MOCK_CAMPAIGNS,
  lastSyncedAt: new Date().toISOString(),
};
// ── END MOCK DATA ─────────────────────────────────────────────────────

function getYearRange(year: number): { startDate: string; endDate: string } {
  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
}

function getMonthRange(offset: number): { startDate: string; endDate: string } {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const end = new Date(target.getFullYear(), target.getMonth() + 1, 0);
  return { startDate: target.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
}

function getQuarterRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  const end = new Date(now.getFullYear(), q * 3 + 3, 0);
  return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
}

const EMPTY_OVERVIEW: GoogleAdsOverview = {
  totalImpressions: 0,
  totalClicks: 0,
  totalCost: 0,
  totalConversions: 0,
  totalConversionValue: 0,
  avgCpc: 0,
  avgCpa: 0,
  avgCtr: 0,
  avgCvr: 0,
  campaigns: [],
  lastSyncedAt: null,
};

const CEOxGoogleAdsContainer = function (): JSX.Element {
  const orgId = getActiveOrgId();
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    preset: 'this_month',
    ...getMonthRange(0),
  });

  const handlePresetChange = useCallback((preset: DatePreset) => {
    const year = new Date().getFullYear();
    switch (preset) {
      case 'this_month': setDateFilter({ preset, ...getMonthRange(0) }); break;
      case 'last_month': setDateFilter({ preset, ...getMonthRange(-1) }); break;
      case 'this_quarter': setDateFilter({ preset, ...getQuarterRange() }); break;
      case 'this_year': setDateFilter({ preset, ...getYearRange(year) }); break;
      case 'last_year': setDateFilter({ preset, ...getYearRange(year - 1) }); break;
      default: setDateFilter((prev) => ({ ...prev, preset }));
    }
  }, []);

  const handleCustomDateChange = useCallback((startDate: string, endDate: string) => {
    setDateFilter({ preset: 'custom', startDate, endDate });
  }, []);

  const { data: status } = useSeoFetch<GoogleAdsConnectionStatus>(
    ['ceox-google-ads-status', orgId],
    () => googleAdsService.getConnectionStatus(),
    { retry: false, enabled: !USE_MOCK }
  );

  const isConnected = USE_MOCK || (status?.isConnected ?? false);

  const { data: overview, isLoading, refetch } = useSeoFetch<GoogleAdsOverview>(
    ['ceox-google-ads-overview', orgId, dateFilter.startDate, dateFilter.endDate],
    () => googleAdsService.getOverview(dateFilter.startDate, dateFilter.endDate),
    { retry: false, enabled: isConnected && !USE_MOCK }
  );

  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = useCallback(async () => {
    if (USE_MOCK) return;
    setIsSyncing(true);
    try {
      await googleAdsService.sync(dateFilter.startDate, dateFilter.endDate);
      refetch();
    } finally {
      setIsSyncing(false);
    }
  }, [refetch, dateFilter.startDate, dateFilter.endDate]);

  const displayOverview = useMemo(
    () => (USE_MOCK ? MOCK_OVERVIEW : (overview ?? EMPTY_OVERVIEW)),
    [overview]
  );

  return (
    <CEOxGoogleAdsComponent
      isConnected={isConnected}
      overview={displayOverview}
      isLoading={USE_MOCK ? false : isLoading}
      isSyncing={isSyncing}
      onSync={handleSync}
      dateFilter={dateFilter}
      onPresetChange={handlePresetChange}
      onCustomDateChange={handleCustomDateChange}
    />
  );
};

export default CEOxGoogleAdsContainer;
